import React from 'react';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Plus,
  Settings,
  Target,
  XCircle,
} from 'lucide-react';

const MESES_INDICE = {
  JAN: 0,
  FEV: 1,
  MAR: 2,
  ABR: 3,
  MAI: 4,
  JUN: 5,
  JUL: 6,
  AGO: 7,
  SET: 8,
  OUT: 9,
  NOV: 10,
  DEZ: 11,
};

const formatarNumero = (valor) => Number(valor || 0).toLocaleString('pt-BR');

const obterPeriodoDate = (item) => {
  if (item.periodoKey) {
    const [ano, mes] = String(item.periodoKey).split('-').map(Number);
    if (ano && mes) return new Date(ano, mes - 1, 1);
  }

  const mes = MESES_INDICE[String(item.mes || '').slice(0, 3).toUpperCase()];
  const ano = Number(item.ano || new Date().getFullYear());

  if (Number.isInteger(mes) && ano) {
    return new Date(ano, mes, 1);
  }

  return null;
};

const periodoJaPassou = (item) => {
  const periodo = obterPeriodoDate(item);
  if (!periodo) return false;

  const hoje = new Date();
  const mesAtual = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

  return periodo < mesAtual;
};

const StatusMeta = ({ statusMeta }) => {
  const status = {
    atingida: {
      icon: CheckCircle2,
      text: 'Meta atingida',
      className: 'border-[color:var(--status-good-border)] bg-[color:var(--status-good-bg)] text-[color:var(--status-good-fg)]',
    },
    proximo: {
      icon: AlertTriangle,
      text: 'Proximo da meta',
      className: 'border-[color:var(--status-warning-border)] bg-[color:var(--status-warning-bg)] text-[color:var(--status-warning-fg)]',
    },
    distante: {
      icon: XCircle,
      text: 'Distante da meta',
      className: 'border-[color:var(--status-danger-border)] bg-[color:var(--status-danger-bg)] text-[color:var(--status-danger-fg)]',
    },
  }[statusMeta];

  const Icon = status.icon;

  return (
    <div className={`inline-flex items-center justify-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${status.className}`}>
      <Icon className="h-3.5 w-3.5" />
      {status.text}
    </div>
  );
};

const GestaoMetas = ({
  dadosEvolucao,
  metasDefinidas,
  podeDefinirMetas,
  inicioVigencia = null,
  onAbrirModalMeta,
}) => {
  return (
    <div className="space-y-8">
      <div className="overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <h2 className="flex items-center text-xl font-bold text-[color:var(--text-primary)]">
              <Target className="mr-2 h-6 w-6 text-[color:var(--accent)]" />
              Gestão de Metas Comerciais
            </h2>
            <p className="mt-1 text-[color:var(--text-muted)]">
              {podeDefinirMetas
                ? 'Defina e acompanhe as metas mensais da equipe comercial'
                : 'Visualize as metas definidas pela direção'}
            </p>
          </div>
          {podeDefinirMetas && (
            <div className="self-start rounded-full bg-[color:var(--surface-muted)] px-3 py-1 text-xs text-[color:var(--accent)] sm:self-auto">
              CEO/Conselho
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {dadosEvolucao.map((item) => {
          // Competência coberta pela régua: os números continuam visíveis, a edição não. O
          // histórico anterior à vigência segue editável — ele permanece válido e é a razão de a
          // aba não desaparecer mais.
          const periodoDate = obterPeriodoDate(item);
          const geridoPelaRegua = Boolean(
            inicioVigencia && periodoDate && periodoDate >= new Date(
              Number(String(inicioVigencia).slice(0, 4)),
              Number(String(inicioVigencia).slice(5, 7)) - 1,
              1,
            ),
          );
          const podeEditarEstePeriodo = podeDefinirMetas && !geridoPelaRegua;
          const metaPeriodo = item.periodoKey
            ? metasDefinidas[item.periodoKey]
            : metasDefinidas[item.mes];
          const metaAtual = metaPeriodo?.vendas || 0;
          const possuiMeta = metaAtual > 0;
          const isHistoricoSemMeta = !possuiMeta && periodoJaPassou(item);
          const vendedoresMeta = metaPeriodo?.vendedores || [];
          const totalMetaVendedores = metaPeriodo?.total_meta_vendedores || 0;
          const saldoDisponivel = metaPeriodo?.saldo_disponivel ?? Math.max(0, metaAtual - totalMetaVendedores);
          const percentualDistribuido = metaAtual > 0 ? (totalMetaVendedores / metaAtual) * 100 : 0;
          const percentualAtingido = metaAtual > 0 ? ((item.vendas / metaAtual) * 100) : 0;
          const statusMeta = percentualAtingido >= 100 ? 'atingida' : percentualAtingido >= 80 ? 'proximo' : 'distante';
          const periodoLabel = item.periodoLabel || `${item.mes}/${item.ano || new Date().getFullYear()}`;

          return (
            <article
              key={item.periodoKey || item.mes}
              className={`group relative min-h-[320px] overflow-hidden rounded-2xl border p-4 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                possuiMeta
                  ? 'border-[color:var(--border)] bg-[color:var(--surface)]'
                  : 'border-dashed border-[color:var(--border)] bg-[color:var(--surface)]'
              }`}
            >
              <div className="pointer-events-none absolute inset-0 opacity-0 transition duration-300 group-hover:opacity-100" style={{ background: 'radial-gradient(circle at 12% 0%, color-mix(in srgb, var(--accent) 12%, transparent), transparent 38%)' }} />

              <div className="relative flex h-full flex-col">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className={`rounded-2xl p-2.5 shadow-sm ${possuiMeta ? 'bg-[color:var(--surface-muted)]' : 'bg-[color:var(--surface-muted)]'}`}>
                    {isHistoricoSemMeta ? (
                      <CalendarClock className="h-5 w-5 text-[color:var(--text-muted)]" strokeWidth={1.5} />
                    ) : (
                      <Target className="h-5 w-5 text-[color:var(--accent)]" strokeWidth={1.5} />
                    )}
                  </div>
                  {podeEditarEstePeriodo && (
                    <button
                      type="button"
                      onClick={() => onAbrirModalMeta(item)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[color:var(--text-muted)] transition hover:bg-[color:var(--surface-muted)] hover:text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                      title={possuiMeta ? 'Editar meta' : 'Definir meta'}
                      aria-label={possuiMeta ? `Editar meta de ${periodoLabel}` : `Definir meta de ${periodoLabel}`}
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold uppercase leading-tight tracking-wide text-[color:var(--text-muted)]">
                      {periodoLabel}
                    </p>
                    <p className="mt-1 text-2xl font-black leading-none text-[color:var(--text-primary)]">
                      {formatarNumero(item.vendas)}
                    </p>
                    <p className="mt-1 text-xs text-[color:var(--text-muted)]">Vendas realizadas</p>
                  </div>

                  {possuiMeta ? (
                    <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="text-[color:var(--text-muted)]">Meta</p>
                          <p className="mt-1 font-bold text-[color:var(--accent)]">{formatarNumero(metaAtual)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[color:var(--text-muted)]">Atingimento</p>
                          <p className={`mt-1 font-bold ${
                            statusMeta === 'atingida'
                              ? 'text-[color:var(--status-good)]'
                              : statusMeta === 'proximo'
                                ? 'text-[color:var(--status-warn)]'
                                : 'text-[color:var(--status-bad)]'
                          }`}
                          >
                            {percentualAtingido.toFixed(1)}%
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-black/10">
                        <div
                          className="h-full rounded-full bg-[linear-gradient(90deg,var(--accent),color-mix(in_srgb,var(--accent)_55%,#22c55e))] transition-[width] duration-300"
                          style={{ width: `${Math.min(percentualAtingido, 100)}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                        {isHistoricoSemMeta ? 'Histórico sem meta' : 'Meta em aberto'}
                      </p>
                      <p className="mt-2 text-xs leading-5 text-[color:var(--text-muted)]">
                        {isHistoricoSemMeta
                          ? 'Este mês permanece apenas como leitura histórica, sem meta associada.'
                          : 'Defina uma meta geral para acompanhar atingimento e distribuição por vendedor.'}
                      </p>
                    </div>
                  )}

                  {possuiMeta && vendedoresMeta.length > 0 && (
                    <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <p className="text-[color:var(--text-muted)]">Vendedores</p>
                          <p className="mt-1 font-bold text-[color:var(--text-primary)]">{vendedoresMeta.length}</p>
                        </div>
                        <div>
                          <p className="text-[color:var(--text-muted)]">Distribuído</p>
                          <p className="mt-1 font-bold text-[color:var(--accent)]">{formatarNumero(totalMetaVendedores)}</p>
                        </div>
                        <div>
                          <p className="text-[color:var(--text-muted)]">Saldo</p>
                          <p className="mt-1 font-bold text-[color:var(--status-good)]">{formatarNumero(saldoDisponivel)}</p>
                        </div>
                      </div>
                      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-black/10">
                        <div
                          className="h-full rounded-full bg-[color:var(--accent)] transition-[width] duration-300"
                          style={{ width: `${Math.min(percentualDistribuido, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-auto pt-4">
                  {possuiMeta ? (
                    <div className="flex justify-center border-t border-[color:var(--border)] pt-3">
                      <StatusMeta statusMeta={statusMeta} />
                    </div>
                  ) : geridoPelaRegua ? (
                    <p className="flex h-11 w-full items-center justify-center rounded-xl border border-dashed border-[color:var(--border)] text-xs font-medium text-[color:var(--text-muted)]">
                      Gerida na aba Comissão
                    </p>
                  ) : podeEditarEstePeriodo && !isHistoricoSemMeta ? (
                    <button
                      type="button"
                      onClick={() => onAbrirModalMeta(item)}
                      className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-sm font-semibold text-[color:var(--accent)] transition hover:border-[color:var(--accent)] hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Definir meta</span>
                    </button>
                  ) : (
                    <div className="flex h-11 items-center justify-center rounded-xl border border-dashed border-[color:var(--border)] text-xs font-semibold uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                      Sem meta registrada
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div>
        <h3 className="mb-4 text-lg font-semibold text-[color:var(--text-primary)]">Resumo do Período (Últimos 6 Meses)</h3>

        <div className="block sm:hidden">
          <div className="grid grid-cols-2 gap-0">
            <div className="rounded-l-xl bg-[color:var(--surface-muted)] p-4 text-center">
              <div className="text-xl font-bold text-[color:var(--accent)]">
                {formatarNumero(dadosEvolucao.reduce((sum, item) => sum + item.vendas, 0))}
              </div>
              <div className="text-xs text-[color:var(--text-muted)]">Total Vendas</div>
            </div>
            <div className="rounded-r-xl bg-[color:var(--surface-muted)] p-4 text-center">
              <div className="text-xl font-bold text-[color:var(--status-good)]">
                {formatarNumero(dadosEvolucao.reduce((sum, item) => {
                  const meta = item.periodoKey ? metasDefinidas[item.periodoKey] : metasDefinidas[item.mes];
                  return sum + (meta?.vendas || 0);
                }, 0))}
              </div>
              <div className="text-xs text-[color:var(--text-muted)]">Total Metas</div>
            </div>
            <div className="rounded-l-xl bg-[color:var(--surface-muted)] p-4 text-center">
              <div className="text-xl font-bold text-[color:var(--accent)]">
                {dadosEvolucao.filter((item) => {
                  const meta = item.periodoKey ? metasDefinidas[item.periodoKey] : metasDefinidas[item.mes];
                  return meta && meta.vendas > 0;
                }).length}
              </div>
              <div className="text-xs text-[color:var(--text-muted)]">Metas Definidas</div>
            </div>
            <div className="rounded-r-xl bg-[color:var(--surface-muted)] p-4 text-center">
              <div className="text-xl font-bold text-[color:var(--status-warn)]">
                {dadosEvolucao.filter((item) => {
                  const meta = item.periodoKey ? metasDefinidas[item.periodoKey] : metasDefinidas[item.mes];
                  return meta && meta.vendas > 0 && item.vendas >= meta.vendas;
                }).length}
              </div>
              <div className="text-xs text-[color:var(--text-muted)]">Metas Atingidas</div>
            </div>
          </div>
        </div>

        <div className="hidden grid-cols-4 gap-0 sm:grid">
          <div className="rounded-l-xl bg-[color:var(--surface-muted)] p-6 text-center">
            <div className="text-2xl font-bold text-[color:var(--accent)]">
              {formatarNumero(dadosEvolucao.reduce((sum, item) => sum + item.vendas, 0))}
            </div>
            <div className="text-sm text-[color:var(--text-muted)]">Total Vendas</div>
          </div>
          <div className="bg-[color:var(--surface-muted)] p-6 text-center">
            <div className="text-2xl font-bold text-[color:var(--status-good)]">
              {formatarNumero(dadosEvolucao.reduce((sum, item) => {
                const meta = item.periodoKey ? metasDefinidas[item.periodoKey] : metasDefinidas[item.mes];
                return sum + (meta?.vendas || 0);
              }, 0))}
            </div>
            <div className="text-sm text-[color:var(--text-muted)]">Total Metas</div>
          </div>
          <div className="bg-[color:var(--surface-muted)] p-6 text-center">
            <div className="text-2xl font-bold text-[color:var(--accent)]">
              {dadosEvolucao.filter((item) => {
                const meta = item.periodoKey ? metasDefinidas[item.periodoKey] : metasDefinidas[item.mes];
                return meta && meta.vendas > 0;
              }).length}
            </div>
            <div className="text-sm text-[color:var(--text-muted)]">Metas Definidas</div>
          </div>
          <div className="rounded-r-xl bg-[color:var(--surface-muted)] p-6 text-center">
            <div className="text-2xl font-bold text-[color:var(--status-warn)]">
              {dadosEvolucao.filter((item) => {
                const meta = item.periodoKey ? metasDefinidas[item.periodoKey] : metasDefinidas[item.mes];
                return meta && meta.vendas > 0 && item.vendas >= meta.vendas;
              }).length}
            </div>
            <div className="text-sm text-[color:var(--text-muted)]">Metas Atingidas</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestaoMetas;
